import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import { apiClient } from '../api/apiConfig';
import { HeartIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import { HealthMetric, HealthGoal } from '../types/health';
import { Alert, Spinner, Badge } from './ui';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';

export interface HealthMetricsListRef {
  refresh: () => void;
}

const HealthMetricsList = forwardRef<HealthMetricsListRef>((props, ref) => {
  const [metrics, setMetrics] = useState<HealthMetric[]>([]);
  const [goals, setGoals] = useState<HealthGoal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [hoveredRowId, setHoveredRowId] = useState<number | null>(null);

  const fetchMetrics = async () => {
    setLoading(true);
    setError(null);
    try {
      const [metricsRes, goalsRes] = await Promise.all([
        apiClient.get('/api/health-metrics/'),
        apiClient.get('/api/health-goals/', { params: { active_only: 'true' } })
      ]);
      setMetrics(metricsRes.data || []);
      setGoals(goalsRes.data || []);
    } catch (err) {
      console.error('Error fetching health data:', err);
      setError('Failed to load health metrics.');
      setMetrics([]);
      setGoals([]);
    } finally {
      setLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    refresh: () => {
      setRefreshKey(prev => prev + 1);
    }
  }));

  useEffect(() => {
    fetchMetrics();
  }, [refreshKey]);

  const filteredMetrics = useMemo(() => {
    if (!searchQuery.trim()) return metrics;
    const query = searchQuery.toLowerCase();
    return metrics.filter(metric =>
      metric.metric_type_display.toLowerCase().includes(query) ||
      metric.notes?.toLowerCase().includes(query)
    );
  }, [metrics, searchQuery]);

  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), 'MMM d, yyyy');
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: SPACING[24]
      }}>
        <Spinner size="md" />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
      {error && <Alert variant="error">{error}</Alert>}

      {goals.length > 0 && (
        <div style={{
          background: `linear-gradient(to right, #F0FDF4, #EFF6FF)`,
          border: `1px solid ${COLORS.status.success}33`,
          borderRadius: RADIUS,
          padding: SPACING[16]
        }}>
          <h3 style={{
            fontSize: TYPOGRAPHY.size.sm,
            fontWeight: TYPOGRAPHY.weight.semibold,
            color: COLORS.text.primary,
            marginBottom: SPACING[12],
            display: 'flex',
            alignItems: 'center'
          }}>
            <ChartBarIcon style={{ height: '20px', width: '20px', marginRight: SPACING[8], color: COLORS.status.success }} />
            Active Health Goals ({goals.length})
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: SPACING[12]
          }}>
            {goals.map(goal => (
              <div key={goal.id} style={{
                backgroundColor: COLORS.bg.panel,
                borderRadius: RADIUS,
                padding: SPACING[12],
                border: `1px solid ${COLORS.border.default}`
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: SPACING[8]
                }}>
                  <span style={{
                    fontSize: TYPOGRAPHY.size.sm,
                    fontWeight: TYPOGRAPHY.weight.medium,
                    color: COLORS.text.primary
                  }}>{goal.name}</span>
                  <Badge variant="success" size="sm">{goal.status_display}</Badge>
                </div>
                <div style={{
                  fontSize: TYPOGRAPHY.size.xs,
                  color: COLORS.text.secondary,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: SPACING[4]
                }}>
                  <div>Target: {goal.target_value} {goal.target_unit}</div>
                  <div>Progress: {goal.progress_percentage}%</div>
                  <div>Streak: {goal.current_streak} days</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ flex: 1 }}>
        <input
          type="text"
          placeholder="Search metrics..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            display: 'block',
            width: '100%',
            paddingLeft: SPACING[16],
            paddingRight: SPACING[16],
            paddingTop: SPACING[8],
            paddingBottom: SPACING[8],
            border: `1px solid ${COLORS.border.default}`,
            borderRadius: RADIUS,
            fontSize: TYPOGRAPHY.size.base,
            fontFamily: TYPOGRAPHY.body.fontFamily,
            boxSizing: 'border-box'
          }}
        />
      </div>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <h3 style={{
          fontSize: TYPOGRAPHY.size.lg,
          fontWeight: TYPOGRAPHY.weight.medium,
          color: COLORS.text.primary,
          display: 'flex',
          alignItems: 'center'
        }}>
          <HeartIcon style={{ width: '20px', height: '20px', marginRight: SPACING[8], color: COLORS.status.error }} />
          Health Metrics
          <span style={{
            marginLeft: SPACING[8],
            display: 'inline-flex',
            alignItems: 'center',
            paddingLeft: SPACING[8],
            paddingRight: SPACING[8],
            paddingTop: SPACING[4],
            paddingBottom: SPACING[4],
            borderRadius: '9999px',
            fontSize: TYPOGRAPHY.size.xs,
            fontWeight: TYPOGRAPHY.weight.medium,
            backgroundColor: COLORS.bg.hover,
            color: COLORS.text.primary
          }}>
            {filteredMetrics.length}
          </span>
        </h3>
      </div>

      {filteredMetrics.length === 0 ? (
        <div style={{
          textAlign: 'center',
          paddingTop: SPACING[48],
          paddingBottom: SPACING[48],
          backgroundColor: COLORS.bg.base,
          borderRadius: RADIUS
        }}>
          <HeartIcon style={{
            margin: '0 auto',
            height: '48px',
            width: '48px',
            color: COLORS.text.tertiary
          }} />
          <p style={{
            marginTop: SPACING[8],
            fontSize: TYPOGRAPHY.size.sm,
            fontWeight: TYPOGRAPHY.weight.medium,
            color: COLORS.text.primary
          }}>No health metrics found</p>
          <p style={{
            marginTop: SPACING[4],
            fontSize: TYPOGRAPHY.size.sm,
            color: COLORS.text.secondary
          }}>Import data to get started</p>
        </div>
      ) : (
        <div style={{
          overflowX: 'auto',
          border: `1px solid ${COLORS.border.default}`,
          borderRadius: RADIUS
        }}>
          <table style={{
            minWidth: '100%',
            backgroundColor: COLORS.bg.panel,
            borderCollapse: 'collapse'
          }}>
            <thead style={{ backgroundColor: COLORS.bg.base }}>
              <tr>
                <th style={{
                  padding: `${SPACING[12]} ${SPACING[16]}`,
                  textAlign: 'left',
                  fontSize: TYPOGRAPHY.size.xs,
                  fontWeight: TYPOGRAPHY.weight.medium,
                  color: COLORS.text.secondary,
                  textTransform: 'uppercase'
                }}>Date</th>
                <th style={{
                  padding: `${SPACING[12]} ${SPACING[16]}`,
                  textAlign: 'left',
                  fontSize: TYPOGRAPHY.size.xs,
                  fontWeight: TYPOGRAPHY.weight.medium,
                  color: COLORS.text.secondary,
                  textTransform: 'uppercase'
                }}>Metric</th>
                <th style={{
                  padding: `${SPACING[12]} ${SPACING[16]}`,
                  textAlign: 'left',
                  fontSize: TYPOGRAPHY.size.xs,
                  fontWeight: TYPOGRAPHY.weight.medium,
                  color: COLORS.text.secondary,
                  textTransform: 'uppercase'
                }}>Value</th>
                <th style={{
                  padding: `${SPACING[12]} ${SPACING[16]}`,
                  textAlign: 'left',
                  fontSize: TYPOGRAPHY.size.xs,
                  fontWeight: TYPOGRAPHY.weight.medium,
                  color: COLORS.text.secondary,
                  textTransform: 'uppercase'
                }}>Source</th>
              </tr>
            </thead>
            <tbody style={{ backgroundColor: COLORS.bg.panel }}>
              {filteredMetrics.map((metric) => (
                <tr
                  key={metric.id}
                  style={{
                    backgroundColor: hoveredRowId === metric.id ? COLORS.bg.hover : COLORS.bg.panel,
                    borderTop: `1px solid ${COLORS.border.default}`
                  }}
                  onMouseEnter={() => setHoveredRowId(metric.id)}
                  onMouseLeave={() => setHoveredRowId(null)}
                >
                  <td style={{
                    padding: `${SPACING[12]} ${SPACING[16]}`,
                    whiteSpace: 'nowrap',
                    fontSize: TYPOGRAPHY.size.sm,
                    color: COLORS.text.primary
                  }}>
                    {formatDate(metric.date)}
                  </td>
                  <td style={{
                    padding: `${SPACING[12]} ${SPACING[16]}`,
                    fontSize: TYPOGRAPHY.size.sm,
                    color: COLORS.text.primary
                  }}>
                    {metric.metric_type_display}
                  </td>
                  <td style={{
                    padding: `${SPACING[12]} ${SPACING[16]}`,
                    whiteSpace: 'nowrap',
                    fontSize: TYPOGRAPHY.size.sm,
                    fontWeight: TYPOGRAPHY.weight.medium,
                    color: COLORS.text.primary
                  }}>
                    {metric.value} {metric.unit}
                  </td>
                  <td style={{
                    padding: `${SPACING[12]} ${SPACING[16]}`,
                    whiteSpace: 'nowrap',
                    fontSize: TYPOGRAPHY.size.sm
                  }}>
                    <Badge variant="default" size="sm">
                      {metric.source_display}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
});

export default HealthMetricsList;
